import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CONSTANTS, StringUtils } from '@pk/powerkioskutils';
import { RouterService } from 'src/app/core/router.service';

import moment from 'moment-timezone';
import { BsModalService } from 'ngx-bootstrap/modal';

import { CookieService } from 'ngx-cookie-service';
import { AgentAgreementModalComponent } from './agent/modals/agent-agreement/agent-agreement.component';
import { AgentBankAccountsReminderModalComponent } from './agent/modals/agent-bank-accounts-reminder-modal/agent-bank-accounts-reminder-modal.component';
import { AgentMarketOpportunitiesModalComponent } from './agent/modals/agent-market-opportunities-modal/agent-market-opportunities-modal.component';
import { AppStateService } from './core/app-state.service';
import { HelpScoutBeaconService } from './core/help-scout-beacon.service';
import { SystemSettingsService } from './core/system-settings.service';
import { UserflowService } from './core/userflow.service';
import { FeedbackModalComponent } from './feedback/modals/feedback-modal/feedback-modal.component';
import { GraphqlService } from './graphql/graphql.service';
import { SecurityService } from './security/security.service';
import { AdvisorAcademyTourModalComponent } from './shared/modals/advisor-academy-tour-modal/advisor-academy-tour-modal.component';
import { TopBannerAlertLearnMoreModalComponent } from './shared/modals/top-banner-alert-learn-more-modal/top-banner-alert-learn-more-modal.component';
import { Agent, Attachment, User } from './shared/models';

declare const $: any;

@Component({
	selector: 'pk-broker-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	standalone: false,
})
export class AppComponent implements OnInit {
	public appReady: boolean;
	public directReady: boolean;
	public billReaderReady: boolean;
	public isPopupWindow: boolean;
	public energyPortalReady: boolean;

	public useDefault: boolean;
	public user: User;
	public agent: Agent;
	public defaultFooterLogoAttachment: Attachment;
	public defaultEnergyPortalLogoAttachment: Attachment;

	public advisorAcademyCookieKey = 'advisor-academy-tour';

	constructor(
		private appStateService: AppStateService,
		private securityService: SecurityService,
		private graphqlService: GraphqlService,
		private router: Router,
		private routerService: RouterService,
		private modalService: BsModalService,
		private route: ActivatedRoute,
		private cookieService: CookieService,
		private helpScoutBeaconService: HelpScoutBeaconService,
		private userflowService: UserflowService,
		private systemSettingsService: SystemSettingsService,
	) {
		this.appStateService.onReady.subscribe(
			(ready: { app: boolean; direct: boolean; billReader: boolean; isPopupWindow: boolean; isEnergyPortal: boolean }) => {
				this.appReady = ready.app;
				this.directReady = ready.direct;
				this.billReaderReady = ready.billReader;
				this.isPopupWindow = ready.isPopupWindow;
				this.energyPortalReady = ready.isEnergyPortal;
			},
		);
	}

	ngOnInit() {
		this.systemSettingsService.load();

		// eslint-disable-next-line space-before-function-paren
		$(document).on('wheel', 'input[type=number]', function () {
			$(this).blur();
		});
	}

	public async onUserUpdated(user: User): Promise<void> {
		this.user = user;
		const isImpersonating = this.securityService.isImpersonating;

		if (
			!this.user.isSaas &&
			!isImpersonating &&
			(this.user.agent || this.user.enterprise?.commissionProfiles.length) &&
			this.securityService.authFields
		) {
			if (
				// parent agent and not also an employee
				// or enterprise with commission profiles
				((!this.user.agent.parent && !this.user.agent.isEmployee) ||
					(this.user.enterprise && this.user.enterprise.commissionProfiles.length && this.user.showEnterpriseCommission)) &&
				(this.user.showTermsOfService || this.user.showCompliance)
			) {
				this.modalService.show(AgentAgreementModalComponent, {
					ignoreBackdropClick: true,
					keyboard: false,
					backdrop: 'static',
					class: 'pk-modal modal-lg',
				});
			} else if (!this.user.agent?.isActive && !this.user.agent.parent) {
				// this.modalService.show(AgentReactivationModalComponent, {
				// 	class: 'pk-modal modal-dialog-centered modal-md',
				// });
			} else if (
				// parent agent and not also an employee have no bank accounts
				// or enterprise commission profiles have no bank accounts
				((!this.user.agent.parent && !this.user.agent.isEmployee) ||
					(this.user.enterprise && this.user.enterprise.commissionProfiles.length && this.user.showEnterpriseCommission)) &&
				((this.user.agent && !this.user.agent.bankAccounts.length) ||
					(this.user.enterprise && this.user.enterprise.commissionProfiles.some(p => !p.bankAccounts.length))) &&
				this.user.agent.hasConfirmedContracts &&
				!this.router.routerState.snapshot.url.includes('security/edit')
			) {
				this.modalService.show(AgentBankAccountsReminderModalComponent, {
					class: 'pk-modal modal-dialog-centered modal-md',
				});
			} else if (
				this.user.agent.marketOpportunitiesAlertEffectiveDate &&
				moment().isAfter(moment(this.user.agent.marketOpportunitiesAlertEffectiveDate))
			) {
				const modalRef = this.modalService.show(AgentMarketOpportunitiesModalComponent, {
					ignoreBackdropClick: true,
					keyboard: false,
					backdrop: 'static',
					class: 'pk-modal modal-lg',
				});
				const marketSub = modalRef.onHidden.subscribe(async () => {
					marketSub.unsubscribe();
					// run this outside the processing loop
					await this.graphqlService.updateAgent(this.user.agentId, {
						extendMarketOpportunitiesAlert: true,
					});
				});
			} else if (!this.user.agent.parent && StringUtils.toBoolean(this.route.snapshot.queryParamMap.get('showReferralPopup'))) {
				const modalRef = this.modalService.show(TopBannerAlertLearnMoreModalComponent, {
					class: 'pk-modal modal-dialog-centered',
					initialState: {
						type: CONSTANTS.bannerAlertTypes.brokerReferral,
					},
				});
				const learnMoreSub = modalRef.onHidden.subscribe(async () => {
					learnMoreSub.unsubscribe();
					await this.routerService.navigate([window.location.pathname]);
				});
			} else if (this.user.showAdvisorAcademyTour && this.user.showAdvisorAcademy) {
				const advisorAcademyCookie = this.cookieService.get(this.advisorAcademyCookieKey + this.user.userId);
				if (!advisorAcademyCookie) {
					const tourModalState = { dontShowAgainCheck: false };
					const modalRef = this.modalService.show(AdvisorAcademyTourModalComponent, {
						class: 'pk-modal modal-lg modal-dialog-centered',
						initialState: {
							tourModalState,
						},
					});
					const academySub = modalRef.onHidden.subscribe(async () => {
						academySub.unsubscribe();
						// if the user checked the box, then deactivate the tour
						if (tourModalState.dontShowAgainCheck) {
							await this.graphqlService.updateUser(this.user.userId, {
								showAdvisorAcademyTour: false,
							});
							await this.securityService.refreshToken();
						} else {
							// otherwise, show it again once the cookie expires
							const nextBusinessDay = moment().add(1, 'day');
							while ([6, 7].includes(nextBusinessDay.isoWeekday())) {
								nextBusinessDay.add(1, 'day');
							}
							this.cookieService.set(this.advisorAcademyCookieKey + this.user.userId, 'set', nextBusinessDay.startOf('day').toDate(), '/');
						}
					});
				}
			}
		}
		if (StringUtils.toBoolean(this.route.snapshot.queryParamMap.get('showFeedback'))) {
			const foreignKeyValue = this.route.snapshot.queryParamMap.get('foreignKeyValue');
			const feedbackTypeId = Number(this.route.snapshot.queryParamMap.get('feedbackTypeId'));
			if (foreignKeyValue && feedbackTypeId) {
				const modalRef = this.modalService.show(FeedbackModalComponent, {
					class: 'pk-modal modal-dialog-centered',
					initialState: {
						feedbackTypeId,
						foreignKeyValue,
					},
				});
				const feedbackSub = modalRef.onHidden.subscribe(async () => {
					feedbackSub.unsubscribe();
					await this.routerService.navigate(['.'], { relativeTo: this.route });
				});
			}
		}
		if (this.user.isSaas || this.user.isAdmin || isImpersonating) {
			window['ga-disable-GTM-5HPC63W'] = true;
		}
		this.helpScoutBeaconService.init();
		await this.userflowService.init();
	}

	public onAgentUpdated(agent: Agent): void {
		this.agent = agent;
	}

	public onUseDefaultUpdated(useDefault: boolean): void {
		this.useDefault = useDefault;
	}

	public onFooterLogoUpdated(logo: Attachment): void {
		this.defaultFooterLogoAttachment = logo;
	}

	public onEnergyPortalLogoUpdated(logo: Attachment): void {
		this.defaultEnergyPortalLogoAttachment = logo;
	}
}
